import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import Logo from "../brand/Logo";
import { Button } from "../foundation";
import Badge from "../foundation/Badge";
import ComingSoonModal from "../marketing/ComingSoonModal";
import { cn } from "@/lib/utils";

const solutionsMenu = [
  { label: "For Businesses", to: "/businesses", desc: "AI underwriting and funding for growing businesses" },
  { label: "Funding Partners", to: "/partners", desc: "Deal flow, ZenScore intelligence, and modern rails" },
  { label: "ZenScore", to: "/zenscore", desc: "Business funding readiness score" },
];

const navLinks = [
  { label: "ZenScore", to: "/zenscore" },
  { label: "Marketplace", to: "/marketplace", badge: "Soon" },
  { label: "Resources", to: "/resources" },
  { label: "About", to: "/about" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const location = useLocation();
  const reduce = useReducedMotion();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
    setSolutionsOpen(false);
  }, [location.pathname]);

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  return (
    <>
      <motion.header
        initial={false}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className={cn(
          "fixed inset-x-0 top-0 z-50 border-b transition-all duration-300",
          scrolled
            ? "bg-bg-base/80 backdrop-blur-xl border-white/[0.08]"
            : "bg-transparent border-transparent backdrop-blur-0",
        )}
        data-testid="navbar"
        data-scrolled={scrolled}
      >
        <nav className="mx-auto flex h-20 w-full max-w-7xl items-center justify-between px-6 sm:px-8 lg:px-12">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 outline-none focus-visible:opacity-90"
            data-testid="nav-logo-link"
            aria-label="ZenMe Capital — Home"
          >
            <Logo size="sm" />
          </Link>

          {/* Center links (desktop) */}
          <ul className="hidden items-center gap-1 lg:flex" data-testid="nav-desktop-links">
            {/* Solutions dropdown */}
            <li
              className="relative"
              onMouseEnter={() => setSolutionsOpen(true)}
              onMouseLeave={() => setSolutionsOpen(false)}
            >
              <button
                type="button"
                className="group inline-flex items-center gap-1 px-3 py-2 text-[14px] text-text-secondary transition-colors hover:text-white"
                aria-expanded={solutionsOpen}
                aria-haspopup="true"
                data-testid="nav-solutions-trigger"
                onClick={() => setSolutionsOpen((v) => !v)}
              >
                <span className="relative">
                  Solutions
                  <span className="absolute inset-x-0 -bottom-1 h-px origin-left scale-x-0 bg-brand-emerald transition-transform duration-300 group-hover:scale-x-100" />
                </span>
                <ChevronDown
                  className={cn(
                    "h-3.5 w-3.5 transition-transform",
                    solutionsOpen && "rotate-180",
                  )}
                />
              </button>
              <AnimatePresence>
                {solutionsOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.18 }}
                    className="absolute left-0 top-full pt-3"
                  >
                    <div
                      className="w-[340px] overflow-hidden rounded-2xl border border-white/10 bg-bg-raised/95 p-2 shadow-2xl backdrop-blur-xl"
                      data-testid="nav-solutions-dropdown"
                    >
                      {solutionsMenu.map((item) => (
                        <Link
                          key={item.to}
                          to={item.to}
                          className="group block rounded-xl p-3 transition-colors hover:bg-white/[0.04]"
                          data-testid={`nav-solutions-link-${item.to.replace("/", "")}`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium text-white group-hover:text-brand-emerald">
                              {item.label}
                            </span>
                          </div>
                          <p className="mt-1 text-xs text-text-muted">{item.desc}</p>
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </li>

            {navLinks.map((link) => (
              <li key={link.to}>
                <NavLink
                  to={link.to}
                  className={({ isActive }) =>
                    cn(
                      "group inline-flex items-center gap-2 px-3 py-2 text-[14px] transition-colors",
                      isActive
                        ? "text-white"
                        : "text-text-secondary hover:text-white",
                    )
                  }
                  data-testid={`nav-link-${link.label.toLowerCase()}`}
                >
                  <span className="relative">
                    {link.label}
                    <span className="absolute inset-x-0 -bottom-1 h-px origin-left scale-x-0 bg-brand-emerald transition-transform duration-300 group-hover:scale-x-100" />
                  </span>
                  {link.badge && (
                    <Badge variant="emerald" className="!py-0 !px-1.5 !text-[9px]">
                      {link.badge}
                    </Badge>
                  )}
                </NavLink>
              </li>
            ))}
          </ul>

          {/* Right actions (desktop) */}
          <div className="hidden items-center gap-2 lg:flex">
            <Button
              href="/contact"
              variant="ghost"
              size="sm"
              data-testid="nav-contact-btn"
              onClick={(e) => {
                e.preventDefault();
                window.location.href = "/contact";
              }}
            >
              Contact
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setWaitlistOpen(true)}
              data-testid="nav-qualify-btn"
            >
              See What You Qualify For
            </Button>
          </div>

          {/* Mobile toggle */}
          <button
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-white transition-colors hover:bg-white/[0.05] lg:hidden"
            aria-label={drawerOpen ? "Close menu" : "Open menu"}
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
          >
            {drawerOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </nav>
      </motion.header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {drawerOpen && (
          <motion.div
            className="fixed inset-0 z-40 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            data-testid="nav-mobile-drawer"
          >
            <div
              className="absolute inset-0 bg-bg-base/95 backdrop-blur-2xl"
              onClick={() => setDrawerOpen(false)}
            />
            <motion.div
              initial={reduce ? { opacity: 0 } : { x: "100%" }}
              animate={reduce ? { opacity: 1 } : { x: 0 }}
              exit={reduce ? { opacity: 0 } : { x: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 32 }}
              className="absolute right-0 top-0 flex h-full w-full max-w-sm flex-col border-l border-white/[0.06] bg-bg-raised px-6 pb-8 pt-24"
            >
              <ul className="flex flex-col gap-1">
                {[
                  { label: "For Businesses", to: "/businesses" },
                  { label: "Funding Partners", to: "/partners" },
                  { label: "ZenScore", to: "/zenscore" },
                  { label: "Marketplace", to: "/marketplace", badge: "Soon" },
                  { label: "Resources", to: "/resources" },
                  { label: "About", to: "/about" },
                  { label: "Contact", to: "/contact" },
                ].map((link, i) => (
                  <motion.li
                    key={link.to}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.04 * i + 0.05, duration: 0.35, ease: "easeOut" }}
                  >
                    <Link
                      to={link.to}
                      className="flex items-center justify-between border-b border-white/[0.05] px-2 py-4 text-lg text-white transition-colors hover:text-brand-emerald"
                      data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      <span>{link.label}</span>
                      {link.badge && (
                        <Badge variant="emerald" className="!py-0 !px-1.5 !text-[9px]">
                          {link.badge}
                        </Badge>
                      )}
                    </Link>
                  </motion.li>
                ))}
              </ul>
              <div className="mt-auto flex flex-col gap-3 pt-8">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => {
                    setDrawerOpen(false);
                    setWaitlistOpen(true);
                  }}
                  data-testid="mobile-nav-qualify-btn"
                >
                  See What You Qualify For
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ComingSoonModal
        open={waitlistOpen}
        onOpenChange={setWaitlistOpen}
        source="nav_qualify"
        mode="qualify"
      />
    </>
  );
};

export default Navbar;
